<template>
  <div class="d-flex flex-column">
    <div class="d-flex flex-column trip-stops">
      <div class="d-flex flex-column justify-space-between">
        <div class="d-flex">
          <div
            v-for="(stop, index) in [stopsData.firstStop, stopsData.lastStop]"
            :key="index"
            class="trip-stops__address-container d-flex flex-column  w-50"
          >
            <span class="caption textTertiary--text pb-2">{{
              stop.stopLabel
            }}</span>
            <div v-if="stop.poiTitle">
              <div class="body-bold brandingPrimaryDark--text mr-4">
                {{ stop.poiTitle }}
              </div>
              <div class="caption mt-1  mr-4">{{ formatStopSecondLine(stop) }}</div>
            </div>
            <div v-else class="mb-2 body-bold brandingPrimaryDark--text">
              {{ stop.address }}
            </div>
          </div>
        </div>
        <div class="d-flex align-items-baseline mt-2">
          <div
            v-for="(stop, index) in [stopsData.firstStop, stopsData.lastStop]"
            :key="index"
            class="d-flex align-items-center w-50"
          >
            <div>
              <div class="d-flex" :class="!stop.date ? 'mb-2' : ''">
                <div>
                  <template
                    v-if="
                      !stop.isRequestedTime && isPlannedTrip && !privateShuttle
                    "
                  >
                    <span class="body-1">{{ stop.date }}</span>
                    <span class="body-bold">
                      {{ `${$t('general.at')} ~ ${stop.time}*` }}
                    </span>
                  </template>
                  <template v-else-if="!stop.isRequestedTime && isPlannedTrip">
                    <span class="body-1">{{ stop.date }}</span>
                    <span class="body-bold">
                      {{ `${$t('general.at')} ~ ${stop.time}` }}
                    </span>
                  </template>

                  <template v-else>
                    <span class="body-1">{{ stop.date }}</span>
                    <span class="body-bold">
                      {{ `${$t('general.at')} ${stop.time}` }}
                    </span>
                  </template>

                  <span
                    :class="{
                      'body-bold': stop.isRequestedTime,
                      'body-1': !stop.isRequestedTime,
                    }"
                  >
                    {{ `(${stop.timeLabel})` }}
                  </span>
                </div>
                <span>
                  <VTooltip
                    v-if="
                      stop.isRequestedTime &&
                      isPlannedTrip &&
                      stop.fromTimeDeviation !== stop.toTimeDeviation
                    "
                    right
                  >
                    <template #activator="{ on, attrs }">
                      <VIcon
                        color="textTertiary"
                        dark
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                      >
                        mdi-information
                      </VIcon>
                    </template>
                    <span>{{
                      $t("trips.timeRangeMightBeMoved", {
                        from: stop.fromTimeDeviation,
                        to: stop.toTimeDeviation,
                      })
                    }}</span>
                  </VTooltip>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SInfoblock
      v-if="!privateShuttle && isPlannedTrip"
      class="w-100"
      :message="$t('trips.timeCanChange')"
      appendIcon="mdi-information"
      appendIconColor="textTertiary"
      :appendTooltipMessage="$t('trips.timeChangeReason')"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'TripViewRide',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import moment from 'moment';
import BookingStopTypeEnum from '@slg/web-customer-shared/src/enum/BookingStopTypeEnum';
import BookingStatusEnum from '@slg/web-customer-shared/src/enum/BookingStatusEnum';
import RideTypeEnum from '@slg/web-customer-shared/src/enum/RideTypeEnum';
import SInfoblock from '@slg/web-customer-shared/src/components/common/SInfoblock';
import STimeConverter from '@slg/web-customer-shared/src/utils/timeConverter';
import { useI18n, useRouteRef } from '../../../../composables';

const props = defineProps({
  stops: {
    type: Array,
    default: () => [],
  },
  estimatedPickUpTime: {
    type: Number,
    default: null,
  },
  estimatedDropOffTime: {
    type: Number,
    default: null,
  },
  privateShuttle: {
    type: Boolean,
    default: true,
  },
  departureDateTime: {
    type: Boolean,
    default: true,
  },
  timeZone: {
    type: String,
    default: moment.tz.guess(),
  },
  bookingStatus: {
    type: String,
    default: null,
  },
});

const { t } = useI18n();
const route = useRouteRef();

function formatStopSecondLine(stop: { stopTitle?: string; address?: string }) {
  return `${stop.stopTitle} ${stop.stopTitle ? '-' : ''} ${stop.address}`;
}

const stopsData = computed(() => {
  const firstStop = (props.stops as Array<Record<string, any>>).find(
    ({ bookingStopType }) =>
      bookingStopType === BookingStopTypeEnum.DEPARTURE_STOP
  );
  const lastStop = (props.stops as Array<Record<string, any>>).find(
    ({ bookingStopType }) =>
      bookingStopType === BookingStopTypeEnum.ARRIVED_STOP
  );

  return {
    firstStop: {
      time: STimeConverter.convertMilliSecondsToTimeZoneTime(
        STimeConverter.roundDate(props.estimatedPickUpTime),
        props.timeZone
      ),
      date: STimeConverter.convertMSToTimeZoneDateTime(
        STimeConverter.roundDate(props.estimatedPickUpTime),
        props.timeZone,
        route.value.params.lang
      ),
      address: firstStop.address,
      poiTitle: firstStop.poiName,
      stopTitle: firstStop.poiStopName,
      isRequestedTime: props.departureDateTime,
      stopLabel: t('trips.from'),
      timeLabel: `${
        props.departureDateTime
          ? t('trips.requestedDeparture')
          : t('trips.estimatedDeparture')
      }`,
      fromTimeDeviation:
        props.departureDateTime && !!props.estimatedPickUpTime
          ? STimeConverter.convertMilliSecondsToTimeZoneTime(
            props.estimatedPickUpTime -
              firstStop.earlyArrivalTimeDeviation,
            props.timeZone
          )
          : null,
      toTimeDeviation:
        props.departureDateTime && !!props.estimatedPickUpTime
          ? STimeConverter.convertMilliSecondsToTimeZoneTime(
            props.estimatedPickUpTime + firstStop.lateArrivalTimeDeviation,
            props.timeZone
          )
          : null,
    },
    lastStop: {
      time: STimeConverter.convertMilliSecondsToTimeZoneTime(
        STimeConverter.roundDate(props.estimatedDropOffTime),
        props.timeZone
      ),
      date: STimeConverter.convertMSToTimeZoneDateTime(
        STimeConverter.roundDate(props.estimatedDropOffTime),
        props.timeZone,
        route.value.params.lang
      ),
      address: lastStop.address,
      poiTitle: lastStop.poiName,
      stopTitle: lastStop.poiStopName,
      isRequestedTime: !props.departureDateTime,
      stopLabel: t('trips.to'),
      timeLabel: `${
        props.departureDateTime
          ? t('trips.estimatedArrival')
          : t('trips.requestedArrival')
      }`,
      fromTimeDeviation:
        !props.departureDateTime && !!props.estimatedDropOffTime
          ? STimeConverter.convertMilliSecondsToTimeZoneTime(
            props.estimatedDropOffTime -
              lastStop.earlyArrivalTimeDeviation,
            props.timeZone
          )
          : null,
      toTimeDeviation:
        !props.departureDateTime && !!props.estimatedDropOffTime
          ? STimeConverter.convertMilliSecondsToTimeZoneTime(
            props.estimatedDropOffTime + lastStop.lateArrivalTimeDeviation,
            props.timeZone
          )
          : null,
    },
  };
});

const isPlannedTrip = computed(() => (
  props.estimatedDropOffTime > moment().valueOf() &&
  props.bookingStatus !== BookingStatusEnum.CANCELLED
));

const getSharedRideType = computed(() => RideTypeEnum.SHARED);
</script>

<style lang="scss" scoped>
.trip-stops {
  min-height: 100px;

  &__stop-number {
    min-width: 24px;
    min-height: 24px;
    max-height: 24px;
    max-width: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__stop-number-separator {
    height: 100%;
    width: 1px;
    border-left: 2px dashed var(--v-textTertiary-base);
  }

  &__address-container {
    min-height: 24px;
  }
}
</style>


